import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { isEmpty } from 'lodash';
import Media from 'react-media';
import { CarouselProvider, Slider, Slide } from 'pure-react-carousel';

import BlockSection from '~/components/wrappers/BlockSection';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import BaseLink from '~/components/base/BaseLink';
import Button from '~/components/base/Button';
import Icon from '~/components/base/Icon';
import ArticleCard from '~/components/cards/ArticleCard';

import { h3 } from '~/styles/typography';
import { calcVw } from '~/styles/helpers';
import { setSmallSpacing, setMediumSpacing } from '~/utilities/spacing';
import {
    sanitizeAndParseHTML,
    dynamicFontSize,
    formatText
} from '~/utilities/helpers';
import { transition } from '~/styles/transition';
import theme from '~/theme';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes,
    AcfLink,
    AcfBackgroundColor
} from '~/utilities/propTypes';

const sharedWrapperStyles = css`
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: ${theme.layout.gutter};
    ${setSmallSpacing('margin-bottom')}
`;

const Title = styled.h3`
    ${transition({}, 'color')};
    ${h3};
    font-size: ${({ largeTitle }) =>
        largeTitle
            ? dynamicFontSize(2.8, 3.4, 4.6)
            : dynamicFontSize(2.2, 3.4, 3.8)};
`;

const DesktopTitleLink = styled(BaseLink)`
    display: none;

    &:hover,
    &:focus {
        ${Title} {
            color: ${theme.colors.primary.yellow};
        }
        svg * {
            color: ${theme.colors.accent.yellow};
        }
    }

    @media (min-width: ${theme.breakpoint.largeTablet}) {
        ${sharedWrapperStyles}
    }
`;

const MobileTitleWrapper = styled.div`
    ${sharedWrapperStyles};
    justify-content: space-between;

    @media (min-width: ${theme.breakpoint.largeTablet}) {
        display: none;
    }
`;

const SliderWrapper = styled.div`
    position: relative;
    width: ${calcVw(100)};
    margin: 0 ${calcVw(-50)};
    left: 50%;
    right: 50%;
    overflow: hidden;
    padding-left: 20px;

    .carousel__slider {
        padding-right: 20vw;
    }

    .article-carousel__tray {
        display: flex;
        gap: 10px;
    }
`;

const SlideInner = styled.div``;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    ${setMediumSpacing('margin-top')}
`;

const ArticleCards = ({
    backgroundColor = 'white',
    link = {},
    cards,
    numberOfColumns = 2,
    largeTitle = false,
    globalBlockOptions = DefaultGlobalBlockOptions,
    attributes = DefaultAcfAttributes
}) => {
    const cardsPerPage = 6;
    const [numberToShow, setNumberToShow] = useState(cardsPerPage);

    return (
        <BlockSection
            {...{ backgroundColor, globalBlockOptions, attributes }}
            padding="medium"
        >
            {!isEmpty(link) ? (
                <>
                    <DesktopTitleLink to={link.url}>
                        <Title>
                            {sanitizeAndParseHTML(formatText(link.title))}
                        </Title>
                        <Icon
                            icon="circle-arrow"
                            color="yellow"
                            size={60}
                            sizeTablet={40}
                            sizeMobile={40}
                        />
                    </DesktopTitleLink>
                    <MobileTitleWrapper>
                        <Title {...{ largeTitle }}>
                            {sanitizeAndParseHTML(formatText(link.title))}
                        </Title>
                        <Button href={link.url}>View All</Button>
                    </MobileTitleWrapper>
                </>
            ) : null}

            <Media query={`(min-width: ${theme.breakpoint.largePhone})`}>
                {(matches) =>
                    matches || numberOfColumns === 2 ? (
                        <Row
                            rowGapTablet="sectionPadding"
                            rowGapPhone="sectionPaddingMedium"
                            rowGap="sectionPaddingSmallMobile"
                            columnGapTablet="sectionPaddingSmallMobile"
                        >
                            {cards.slice(0, numberToShow).map((card) => (
                                <Column
                                    key={card.uri}
                                    spanLargePhone={12 / numberOfColumns}
                                    span={12}
                                >
                                    <ArticleCard {...card} />
                                </Column>
                            ))}
                        </Row>
                    ) : (
                        <CarouselProvider
                            naturalSlideWidth={330}
                            naturalSlideHeight={310}
                            totalSlides={cards.length}
                            isIntrinsicHeight={true}
                            visibleSlides={1}
                        >
                            <SliderWrapper>
                                <Slider classNameTray="article-carousel__tray">
                                    {cards.map((card, i) => (
                                        <Slide index={i} key={card.uri}>
                                            <SlideInner>
                                                <ArticleCard {...card} />
                                            </SlideInner>
                                        </Slide>
                                    ))}
                                </Slider>
                            </SliderWrapper>
                        </CarouselProvider>
                    )
                }
            </Media>

            {numberToShow < cards.length ? (
                <ButtonWrapper>
                    <Button
                        onClick={() =>
                            setNumberToShow(numberToShow + cardsPerPage)
                        }
                    >
                        Load More
                    </Button>
                </ButtonWrapper>
            ) : null}
        </BlockSection>
    );
};

ArticleCards.propTypes = {
    ...AcfBackgroundColor,
    link: AcfLink,
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            excerpt: PropTypes.string,
            featuredImage: PropTypes.shape({}),
            uri: PropTypes.string.isRequired
        })
    ).isRequired,
    numberOfColumns: PropTypes.oneOf([2, 3]),
    largeTitle: PropTypes.bool,
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default ArticleCards;
